import { PrimaryButton } from "@components/Buttons/PrimaryButton";
import { useAssignPhoneNumberMutation } from "@components/subscriptions/hooks/useAssignPhoneNumber.mutation";
import { Box, Chip, Typography } from "@mui/material";
import { useState } from "react";
import { CompanyNumber } from "..";
import { FlowMobilePlanConfig } from "../../../";

interface AssignExistingNumberProps {
  plan: FlowMobilePlanConfig;
  plans: FlowMobilePlanConfig[];
  availableNumbers: CompanyNumber[];
}

export function AssignExistingNumber({ plan, plans, availableNumbers }: AssignExistingNumberProps) {
  const [selectedCompanyNumberUid, setSelectedCompanyNumberUid] = useState<string>("");
  const { mutateAsync: assignNumber, isPending } = useAssignPhoneNumberMutation();

  const selectedCompanyNumber = availableNumbers.find((companyNumber) => companyNumber.uid === selectedCompanyNumberUid);

  const handleAssignNumber = async () => {
    if (!selectedCompanyNumber) return;

    try {
      await assignNumber({
        subscriptionId: plan.id,
        externalId: selectedCompanyNumber.uid,
      });
    } catch (error) {
      console.error("Failed to assign number", error);
    }
  };

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
      <Box>
        <Typography sx={{ fontSize: 20, fontWeight: 800, color: "var(--fx-surface-text, #0F172A)", mb: 0.5 }}>Assign existing number to {plan.name}</Typography>
        <Typography sx={{ fontSize: 14, color: "var(--fx-surface-textsecondary, #64748B)", lineHeight: 1.4 }}>
          Select a company number to assign it to <b>{plan.name}</b>.
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxHeight: 350, overflowY: "auto", pr: 1 }}>
        {availableNumbers.map((companyNumber) => {
          const isSelected = companyNumber.uid === selectedCompanyNumberUid;
          const assignedPlan = plans.find((p) => p.externalId === companyNumber.uid);

          return (
            <Box
              key={companyNumber.uid}
              onClick={() => setSelectedCompanyNumberUid(companyNumber.uid)}
              sx={{
                p: 2,
                borderRadius: "12px",
                border: isSelected ? "2px solid #FB8E00" : "1px solid rgba(0,0,0,0.08)",
                cursor: "pointer",
                bgcolor: isSelected ? "rgba(251,142,0,0.04)" : "var(--fx-surface-base, #FFFFFF)",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  bgcolor: isSelected ? "rgba(251,142,0,0.06)" : "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.02)",
                  borderColor: isSelected ? "#FB8E00" : "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.15)",
                },
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: isSelected ? "#FB8E00" : "var(--fx-surface-textsecondary, #64748B)" }}>{companyNumber.phoneNumber}</Typography>
                  {assignedPlan && (
                    <Chip
                      label={`Assigned to ${assignedPlan.name}`}
                      size="small"
                      sx={{
                        bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
                        color: "var(--fx-surface-textsecondary, #64748B)",
                        fontWeight: 800,
                        fontSize: 11,
                        height: 20,
                      }}
                    />
                  )}
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "var(--fx-surface-texttertiary, #94A3B8)", mt: 0.5 }}>
                  {companyNumber.source} · {companyNumber.status}
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  border: isSelected ? "6px solid #FB8E00" : "2px solid var(--fx-surface-borderstrong, #CBD5E1)",
                  bgcolor: "var(--fx-surface-base, #FFFFFF)",
                }}
              />
            </Box>
          );
        })}
      </Box>

      <PrimaryButton
        disabled={!selectedCompanyNumber || isPending}
        onClick={handleAssignNumber}
        sx={{
          borderRadius: "8px",
          py: 1.2,
          fontWeight: 800,
          fontSize: 15,
          mt: 1,
        }}
      >
        {isPending ? "Assigning..." : `Assign ${selectedCompanyNumber ? selectedCompanyNumber.phoneNumber : "number"}`}
      </PrimaryButton>
    </Box>
  );
}
